import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "@marekl/adminer/api";
import { apiDelete, apiGet, apiPost, apiPut } from "../api";
import type { Datified, Schemas } from "../../api/types";
import { POLL_BASE_MS } from "./intervals";

export type Company = Datified<Schemas["Company"], "created_at" | "updated_at">;
export type CompanyInput = Schemas["CompanyInput"];
export type BillingDetails = Datified<Schemas["BillingDetails"], "created_at">;
export type BillingDetailsInput = Schemas["BillingDetailsInput"];
export type CompanyRole = Schemas["CompanyRole"];
export type CompanyMember = Datified<Schemas["CompanyMember"], "created_at">;

/** The companies the signed-in user may see (every one for an admin). */
export function useCompanies() {
  return useQuery<Company[]>({
    queryKey: ["companies"],
    queryFn: async () => (await apiGet<{ items: Company[] }>("/companies")).items,
    refetchInterval: POLL_BASE_MS,
  });
}

export function useCompany(id: string | undefined) {
  return useQuery<Company>({
    queryKey: ["companies", id],
    queryFn: () => apiGet<Company>(`/companies/${id}`),
    enabled: !!id,
  });
}

export function useCreateCompany() {
  const qc = useQueryClient();
  return useMutation<Company, unknown, CompanyInput>({
    meta: { suppressErrorToast: true },
    mutationFn: (input) => apiPost<Company>("/companies", input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["companies"] }),
  });
}

export function useUpdateCompany(id: string) {
  const qc = useQueryClient();
  return useMutation<Company, unknown, CompanyInput>({
    meta: { suppressErrorToast: true },
    mutationFn: (input) => apiPut<Company>(`/companies/${id}`, input),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["companies"] }),
  });
}

export function useCompanyMembers(id: string, enabled: boolean) {
  return useQuery<CompanyMember[]>({
    queryKey: ["companies", id, "members"],
    queryFn: async () => (await apiGet<{ items: CompanyMember[] }>(`/companies/${id}/members`)).items,
    enabled,
  });
}

export function useSetCompanyMember(id: string) {
  const qc = useQueryClient();
  return useMutation<CompanyMember, unknown, { userId: string; role: CompanyRole }>({
    meta: { suppressErrorToast: true },
    mutationFn: ({ userId, role }) => apiPut<CompanyMember>(`/companies/${id}/members/${userId}`, { role }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["companies", id, "members"] }),
  });
}

export function useRemoveCompanyMember(id: string) {
  const qc = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: async (userId) => {
      await apiDelete(`/companies/${id}/members/${userId}`);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["companies", id, "members"] }),
  });
}

/** A user's role in each company they belong to, by company id. */
export type CompanyAccess = Record<string, CompanyRole>;

/**
 * Every user's company roles (admins only): the members of every company,
 * folded per user. The instance has a handful of companies, so this is one
 * small request per company.
 */
export function useAllCompanyAccess(enabled: boolean) {
  const companies = useCompanies();
  const list = enabled ? (companies.data ?? []) : [];
  const members = useQueries({
    queries: list.map((c) => ({
      queryKey: ["companies", c.id, "members"],
      queryFn: async () => (await apiGet<{ items: CompanyMember[] }>(`/companies/${c.id}/members`)).items,
    })),
  });
  const byUser: Record<string, CompanyAccess> = {};
  members.forEach((q, i) => {
    for (const m of q.data ?? []) (byUser[m.user_id] ??= {})[list[i].id] = m.role;
  });
  return {
    companies: list,
    byUser,
    isLoading: companies.isLoading || members.some((q) => q.isLoading),
    error: companies.error ?? members.find((q) => q.error)?.error ?? null,
  };
}

/** Applies a user's new company roles: PUT the changed ones, DELETE the removed. */
export function useSaveCompanyAccess() {
  const qc = useQueryClient();
  return useMutation<void, unknown, { userId: string; before: CompanyAccess; after: CompanyAccess }>({
    meta: { suppressErrorToast: true },
    mutationFn: async ({ userId, before, after }) => {
      for (const [companyId, role] of Object.entries(after)) {
        if (before[companyId] !== role) await apiPut(`/companies/${companyId}/members/${userId}`, { role });
      }
      for (const companyId of Object.keys(before)) {
        if (after[companyId]) continue;
        try {
          await apiDelete(`/companies/${companyId}/members/${userId}`);
        } catch (err) {
          // Already gone (an earlier, partly failed save): that is the goal.
          if (!(err instanceof ApiError && err.status === 404)) throw err;
        }
      }
    },
    // Partly applied or not, the lists must show what the server now holds.
    onSettled: () => void qc.invalidateQueries({ queryKey: ["companies"] }),
  });
}

/** A company's display name: its current billing details. */
export function companyName(c: Company | undefined): string {
  return c?.billing_details.business_name ?? "";
}

/** Role order of internal/tenant: viewer < editor < manager. */
const RANK: Record<CompanyRole, number> = { viewer: 0, editor: 1, manager: 2 };
export function atLeast(role: CompanyRole | undefined, want: CompanyRole): boolean {
  return role !== undefined && RANK[role] >= RANK[want];
}
